import { useState, useEffect } from 'react';
import { getApp } from 'firebase/app';
import { getFirestore, doc, onSnapshot } from 'firebase/firestore';

const CACHE_KEY = 'smc_app_config_v1';
const CACHE_TTL = 5 * 60 * 1000;

export const DEFAULT_CONFIG = {
  features: {
    attendanceEnabled: true,
    chatEnabled: true,
    meetingsEnabled: true,
    cadViewerEnabled: true,
    achievementsEnabled: true,
    socialFeedEnabled: true,
    financePortalEnabled: true,
  },
  uploads: {
    maxVideoSizeMB: 2048,
    maxImageSizeMB: 50,
    maxAttachmentSizeMB: 100,
    allowedVideoFormats: ['mp4', 'mov', 'avi', 'webm'],
    allowedImageFormats: ['jpg', 'jpeg', 'png', 'webp', 'heic'],
  },
  session: {
    timeoutMinutes: 480,
    maxDevices: 3,
    requireReauthAfterDays: 30,
  },
  maintenance: {
    enabled: false,
    message: "We're performing scheduled maintenance. We'll be right back.",
    allowedRoles: ['admin'],
    estimatedEndTime: '',
  },
  notifications: {
    quietHoursStart: 22,
    quietHoursEnd: 7,
    quietHoursEnabled: true,
    checkInReminderHour: 10,
  },
  themes: {
    availableThemes: ['deepSpace', 'obsidian', 'midnight', 'carbon', 'slatePro', 'socialPro'],
    defaultTheme: 'deepSpace',
    allowUserThemeChange: true,
  },
};

function readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw);
    if (Date.now() - ts < CACHE_TTL) return data;
  } catch (_) {}
  return null;
}

function mergeWithDefaults(data) {
  return {
    features:      { ...DEFAULT_CONFIG.features,      ...(data?.features || {}) },
    uploads:       { ...DEFAULT_CONFIG.uploads,       ...(data?.uploads || {}) },
    session:       { ...DEFAULT_CONFIG.session,       ...(data?.session || {}) },
    maintenance:   { ...DEFAULT_CONFIG.maintenance,   ...(data?.maintenance || {}) },
    notifications: { ...DEFAULT_CONFIG.notifications, ...(data?.notifications || {}) },
    themes:        { ...DEFAULT_CONFIG.themes,        ...(data?.themes || {}) },
  };
}

export function useAppConfig() {
  const [config, setConfig] = useState(() => {
    const cached = readCache();
    return cached ? mergeWithDefaults(cached) : DEFAULT_CONFIG;
  });

  useEffect(() => {
    let db;
    try { db = getFirestore(getApp()); } catch (_) { return; }
    const unsub = onSnapshot(doc(db, 'app_config', 'settings'), snap => {
      if (snap.exists()) {
        const merged = mergeWithDefaults(snap.data());
        setConfig(merged);
        try { localStorage.setItem(CACHE_KEY, JSON.stringify({ data: snap.data(), ts: Date.now() })); } catch (_) {}
      }
    }, () => {});
    return unsub;
  }, []);

  return config;
}
